import React from 'react';

import {ChartConfiguration} from 'chart.js';
import {useTranslation} from 'react-i18next';

import {AuditStatisticsEntity, NOTIFICATION_STATUS_CODE} from '@entities/audit-statistics/entity';
import {ViolationDictionaryItem} from '@entities/violations/types';

import {ChartBox, ChartHeader, ChartTitle, Legend, LegendDot, LegendItem} from '../styled';

import {createChart, getLocalizedTitle, MONTH_KEYS} from './chartUtils';

interface Props {
    statistics: AuditStatisticsEntity;
    notificationStatuses: ViolationDictionaryItem[];
}

export const NotificationsByStatusChart = ({statistics, notificationStatuses}: Props) => {
    const {t, i18n} = useTranslation();
    const canvasRef = React.useRef<HTMLCanvasElement | null>(null);
    const normalizedData = React.useMemo(() => statistics.normalizeNotificationsByStatus(), [
        statistics,
    ]);
    const selectedMonths = normalizedData.map((item) => item.month);
    const labels = [NOTIFICATION_STATUS_CODE.SENT, NOTIFICATION_STATUS_CODE.SEND_ERROR].map(
        (code) =>
            getLocalizedTitle(
                notificationStatuses.find((item) => item.code === code),
                i18n.language,
            ) ||
            t(
                code === NOTIFICATION_STATUS_CODE.SENT
                    ? 'page_audit.page_statistic.notifications-by-status-success'
                    : 'page_audit.page_statistic.notifications-by-status-failure',
            ),
    );

    React.useEffect(() => {
        if (!canvasRef.current) return undefined;

        const config: ChartConfiguration<'bar'> = {
            type: 'bar',
            data: {
                labels: MONTH_KEYS.filter((_, index) =>
                    selectedMonths.includes(index + 1),
                ).map((month) => t(`dictionaries.months.${month}.short`)),
                datasets: [
                    {
                        label: labels[0],
                        data: normalizedData.map((item) => item[NOTIFICATION_STATUS_CODE.SENT]),
                        backgroundColor: '#52CB50',
                    },
                    {
                        label: labels[1],
                        data: normalizedData.map(
                            (item) => item[NOTIFICATION_STATUS_CODE.SEND_ERROR],
                        ),
                        backgroundColor: '#F54848',
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: {display: false},
                    tooltip: {
                        padding: 16,
                        callbacks: {
                            label: () => '',
                            beforeLabel: (item) =>
                                `${labels[item.datasetIndex]}: ${item.formattedValue}`,
                        },
                    },
                },
                scales: {
                    y: {
                        beginAtZero: true,
                        ticks: {precision: 0},
                    },
                },
            },
        };

        const chart = createChart(canvasRef.current, config);
        return () => chart.destroy();
    }, [labels, normalizedData, selectedMonths, t]);

    return (
        <>
            <ChartHeader>
                <ChartTitle>
                    {t('page_audit.page_statistic.notifications-by-status-title')}
                </ChartTitle>
                <Legend>
                    <LegendItem>
                        <LegendDot color="#52CB50" />
                        {labels[0]}
                    </LegendItem>
                    <LegendItem>
                        <LegendDot color="#F54848" />
                        {labels[1]}
                    </LegendItem>
                </Legend>
            </ChartHeader>
            <ChartBox>
                <canvas ref={canvasRef} />
            </ChartBox>
        </>
    );
};
